import { Box, Typography } from '@mui/material';
import useSendById from 'hooks/queries/useSendById';
import {
    EngagementAPIOperator,
    EngagementName,
    EngagementUIOperator,
    SegmentDefinition,
    SegmentEngagement,
} from 'domains/segments/types';
import MatchTypeChip from '../MatchTypeChip';
import { CampaignChannelLabels } from 'domains/campaigns/types';

type Props = {
    details: SegmentEngagement[];
    matchType: SegmentDefinition['matchType'];
    shouldShowMatchType: boolean;
};

// TODO: refactor this, especially UserAction and CurationName
const EngagementDetails = ({ details, matchType, shouldShowMatchType }: Props) => (
    <>
        {details.map((engagement: SegmentEngagement, index) => {
            const delivered =
                (engagement.name === EngagementName.RECEIVED_SENDS ||
                    engagement.name === EngagementName.DELIVERED_SENDS) &&
                engagement.operator === EngagementAPIOperator.CONTAINS;
            const didNotDeliver =
                (engagement.name === EngagementName.RECEIVED_SENDS ||
                    engagement.name === EngagementName.DELIVERED_SENDS) &&
                engagement.operator === EngagementAPIOperator.DOES_NOT_CONTAIN;
            const clicked =
                engagement.name === EngagementName.CLICKED_SENDS &&
                engagement.operator === EngagementAPIOperator.CONTAINS;
            const didNotClick =
                engagement.name === EngagementName.CLICKED_SENDS &&
                engagement.operator === EngagementAPIOperator.DOES_NOT_CONTAIN;
            const opened =
                engagement.name === EngagementName.OPENED_SENDS &&
                engagement.operator === EngagementAPIOperator.CONTAINS;
            const didNotOpen =
                engagement.name === EngagementName.OPENED_SENDS &&
                engagement.operator === EngagementAPIOperator.DOES_NOT_CONTAIN;
            const didReply =
                engagement.name === EngagementName.REPLIED_SENDS &&
                engagement.operator === EngagementAPIOperator.CONTAINS;
            const didNotReply =
                engagement.name === EngagementName.REPLIED_SENDS &&
                engagement.operator === EngagementAPIOperator.DOES_NOT_CONTAIN;
            const callAnswered =
                engagement.name === EngagementName.CALL_ANSWERED_SENDS &&
                engagement.operator === EngagementAPIOperator.CONTAINS;
            const callNotAnswered =
                engagement.name === EngagementName.NOT_PICKED_SENDS &&
                engagement.operator === EngagementAPIOperator.CONTAINS;
            const voicemailSent =
                engagement.name === EngagementName.VOICEMAIL_SENT_SENDS &&
                engagement.operator === EngagementAPIOperator.CONTAINS;
            const callFailed =
                engagement.name === EngagementName.CALL_FAILED_SENDS &&
                engagement.operator === EngagementAPIOperator.CONTAINS;
            const callTransfered =
                engagement.name === EngagementName.CALL_TRANSFERED_SENDS &&
                engagement.operator === EngagementAPIOperator.CONTAINS;
            const mailSent =
                engagement.name === EngagementName.SENT_SENDS && engagement.operator === EngagementAPIOperator.CONTAINS;
            const mailNotSent =
                engagement.name === EngagementName.SENT_SENDS &&
                engagement.operator === EngagementAPIOperator.DOES_NOT_CONTAIN;

            const hasValues = !!engagement?.additionalValues?.length || !!engagement.segmentLinkValue;

            const UserAction = () => (
                <>
                    {delivered && ` ${EngagementUIOperator.DID_RECEIVE} `}
                    {didNotDeliver && ` ${EngagementUIOperator.DID_NOT_RECEIVE} `}
                    {clicked && ` ${EngagementUIOperator.DID_CLICK} `}
                    {didNotClick && ` ${EngagementUIOperator.DID_NOT_CLICK} `}
                    {opened && ` ${EngagementUIOperator.DID_OPEN} `}
                    {didNotOpen && ` ${EngagementUIOperator.DID_NOT_OPEN} `}
                    {didReply && ` ${EngagementUIOperator.DID_REPLY} ${hasValues ? '' : 'to '}`}
                    {didNotReply && ` ${EngagementUIOperator.DID_NOT_REPLY} to `}
                    {callAnswered && ` ${EngagementUIOperator.CALL_ANSWERED} `}
                    {callNotAnswered && ` ${EngagementUIOperator.CALL_NOT_ANSWERED} `}
                    {voicemailSent && ` ${EngagementUIOperator.VOICEMAIL} `}
                    {callFailed && ` ${EngagementUIOperator.CALL_FAILED} `}
                    {callTransfered && ` ${EngagementUIOperator.CALL_TRANSFERRED} `}
                    {mailSent && ` was ${EngagementUIOperator.SENT} `}
                    {mailNotSent && ` was ${EngagementUIOperator.NOT_SENT} `}
                </>
            );
            const CurationName = () => {
                const { data, isError, isLoading } = useSendById(engagement.value);
                if (isError) {
                    return null;
                }
                if (isLoading) {
                    return <>Loading....</>;
                }
                return <>{data.name}</>;
            };

            const renderValue = (value: string, isLast: boolean = true) => {
                const channelLabel = engagement?.channel ? CampaignChannelLabels[engagement.channel] : 'email';
                const isEmail = channelLabel === CampaignChannelLabels.EMAIL;
                const isSMS = channelLabel === CampaignChannelLabels.SMS;

                return (
                    <>
                        <Typography
                            component="span"
                            color="secondary"
                            sx={{ textWrap: 'wrap', textDecoration: isEmail ? 'underline' : 'none' }}
                        >
                            {isSMS ? `'${value}'` : value}
                        </Typography>
                        {!isLast && (
                            <Typography component="span" sx={{ textWrap: 'wrap' }}>
                                {', '}
                            </Typography>
                        )}
                        {isLast && (
                            <Typography component="span" sx={{ textWrap: 'wrap' }}>
                                {isEmail ? ' in ' : ' to '}
                            </Typography>
                        )}
                    </>
                );
            };

            const renderValues = () => {
                if (engagement?.segmentLinkValue) {
                    return renderValue(engagement.segmentLinkValue);
                } else if (engagement?.additionalValues) {
                    return engagement.additionalValues.map((value, index) => {
                        const isLast = index + 1 === engagement.additionalValues.length;
                        return renderValue(value, isLast);
                    });
                } else return;
            };

            return (
                <Box data-testid="filter" key={index} textAlign="right">
                    {(shouldShowMatchType || index !== 0) && <MatchTypeChip matchType={matchType} />}
                    <Typography component="span" color="secondary" sx={{ textWrap: 'wrap' }}>
                        Contact Activity
                    </Typography>
                    <Typography component="span" sx={{ textWrap: 'wrap' }}>
                        <UserAction />
                    </Typography>
                    {renderValues()}
                    <Typography component="span" color="secondary" sx={{ textWrap: 'wrap' }}>
                        <CurationName />
                    </Typography>
                </Box>
            );
        })}
    </>
);

export default EngagementDetails;
